import React from 'react';

interface QuickFiltersProps {
  activeFilters: Set<string>;
  onFilterToggle: (filter: string) => void;
  stats?: {
    active_runs: number;
    scheduled_runs: number;
    cancelled_runs: number;
    overdue_runs: number;
    recent_calls_24h: number;
  };
}

const QUICK_FILTERS = [
  {
    key: 'active',
    label: 'Active',
    color: 'bg-green-100 text-green-800 hover:bg-green-200',
  },
  {
    key: 'scheduled',
    label: 'Scheduled',
    color: 'bg-purple-100 text-purple-800 hover:bg-purple-200',
  },
  {
    key: 'overdue',
    label: 'Overdue',
    color: 'bg-red-100 text-red-800 hover:bg-red-200',
  },
  {
    key: 'recent',
    label: 'Recent 24h',
    color: 'bg-blue-100 text-blue-800 hover:bg-blue-200',
  },
  {
    key: 'failed',
    label: 'Failed',
    color: 'bg-gray-100 text-gray-800 hover:bg-gray-200',
  },
  {
    key: 'police',
    label: 'Police',
    color: 'bg-indigo-100 text-indigo-800 hover:bg-indigo-200',
  },
  {
    key: 'cancelled',
    label: 'Cancelled',
    color: 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200',
  },
] as const;

export const QuickFilters: React.FC<QuickFiltersProps> = ({
  activeFilters,
  onFilterToggle,
  stats,
}) => {
  const getFilterCount = (filterKey: string): number => {
    if (!stats) return 0;

    switch (filterKey) {
      case 'active':
        return stats.active_runs;
      case 'scheduled':
        return stats.scheduled_runs;
      case 'overdue':
        return stats.overdue_runs;
      case 'recent':
        return stats.recent_calls_24h;
      case 'cancelled':
        return stats.cancelled_runs;
      default:
        return 0;
    }
  };

  return (
    <div className="flex items-center gap-2 px-4 py-2 bg-gray-50 border-b border-gray-200">
      <span className="text-xs font-medium text-gray-600 mr-2">
        Quick filters:
      </span>

      <div className="flex items-center gap-1">
        {QUICK_FILTERS.map((filter) => {
          const isActive = activeFilters.has(filter.key);
          const count = getFilterCount(filter.key);

          return (
            <button
              key={filter.key}
              type="button"
              onClick={() => onFilterToggle(filter.key)}
              className={`
                px-2 py-1 rounded text-xs font-medium transition-colors
                ${
                  isActive
                    ? `${filter.color} ring-2 ring-offset-1 ring-current`
                    : `${filter.color} opacity-60 hover:opacity-100`
                }
              `}
            >
              {filter.label}
              {count > 0 && <span className="ml-1 font-mono">{count}</span>}
            </button>
          );
        })}
      </div>

      {activeFilters.size > 0 && (
        <button
          type="button"
          onClick={() =>
            activeFilters.forEach((filter) => onFilterToggle(filter))
          }
          className="ml-2 px-2 py-1 text-xs text-gray-500 hover:text-gray-700 border border-gray-300 rounded"
        >
          Clear all
        </button>
      )}
    </div>
  );
};
